import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

// Modular D3 imports for performance
import { select, pointer, Selection } from 'd3-selection';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { line, curveMonotoneX } from 'd3-shape';
import 'd3-transition'; // Required for .transition() method
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  ThemeService,
  Theme,
} from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type colorLegend = `#${string}`;

// --- Added Interfaces for better Type Safety ---
export interface BarChartData {
  start: string;
  end: string;
  month_short?: string;
  color?: string;
  [key: string]: any; // For dynamic keys like 'total', 'dental', etc.
}

export interface LegendData {
  year: string;
  color: colorLegend;
}

export interface ColorMap {
  data1: colorLegend | '';
  data2: colorLegend | '';
}

export interface TooltipConfig {
  label: string;
  key: string;
  type?: 'percent' | string;
}
// ---

@Component({
  selector: 'app-single-bar',
  templateUrl: './single-bar.component.html',
  styleUrls: ['./single-bar.component.scss'],
  providers: [DatePipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class SingleBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  @Input() data1: BarChartData[] = [];
  @Input() data2: BarChartData[] = [];
  @Input() legendData: LegendData[] = [];
  @Input() barClusterPixel: number = 20;
  @Input() marginTop: number = 30;
  @Input() marginRight: number = 0;
  @Input() marginBottom: number = 40;
  @Input() marginLeft: number = 60;
  private get margin() {
    return {
      top: this.marginTop,
      right: this.marginRight,
      bottom: this.marginBottom,
      left: this.marginLeft
    };
  }
  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private combinedData: (BarChartData & {
    color: colorLegend;
    seriesIndex?: number;
  })[][] = [];
  fullBarWidth: number = 0;
  gap: number = 2;

  @Input() colors: ColorMap = {
    data1: '',
    data2: '',
  };
  @Input() valueKey = 'total';
  @Input() otherKey: string = '';
  @Input() valueType: '$' | '%' = '%';
  @Input() barTextposition: string = 'top';
  @Input() barTextColor: colorLegend = '' as colorLegend;
  @Input() tooltipData: TooltipConfig[] = [
    { label: 'Dental', key: 'dental' },
    { label: 'HealthCare', key: 'healthcare' },
    { label: 'Non HealthCare', key: 'non_healthcare' },
  ];
  @Input() labelKey: any;
  @Input() showFullLengthLabel: boolean = false;
  @Input() flatBars: boolean = false;
  @Input() showYAxis: boolean = true;
  @Input() yAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllYticks: boolean = false;
  @Input() showNameLabel: boolean = true;
  @Input() rotateLabel: boolean = false;
  @Input() showBarValue: boolean = false;
  @Input() barWidthScale: number = 1; // default normal height
  @Input() chartHeight: number = 0; // default normal height
  @Input() minHeight: number = 300; // minimum chart height
  @Input() tooltipValueLabels: string[] = [];
  @Input() barStyle: 'solid' | 'gradient' = 'solid';
  @Input() direction: 'top-to-bottom' | 'bottom-to-top' = 'bottom-to-top';

  private readonly chartId = `chart-${Math.random().toString(36).substr(2, 9)}`;

  private themeService = inject(ThemeService);

  constructor(private datePipe: DatePipe) { }

  ngAfterViewInit() {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.renderChart(); // Redraw chart with new theme colors
    });

    this.observeResize();
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data1'] || changes['data2']) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // Bar colors
      primaryBar: this.colors.data1 || (isDark ? '#a78bfa' : '#8b5cf6'), // purple-400 / purple-600
      secondaryBar: this.colors.data2 || (isDark ? '#a78bfa' : '#8b5cf6'), // purple-400 / purple-600

      // Text colors
      barTextColor: this.barTextColor || (isDark ? '#f4f4f5' : '#000000'), // slate-100 / black
      axisText: isDark ? '#A1A1AA' : '#7D7D93', // slate-300 / dark-gray
      axisLine: isDark ? '#2A2A2E' : '#EAEAEA', // slate-500 / slate-400 - More prominent
      axisGridLine: isDark ? '#232327' : '#EAEAEA', // slate-500 / slate-400 - More prominent

      // Bar background/outline
      barOutlineFill: isDark ? '#1e293b3a' : '#8a5cf613', // slate-800 / white
      barOutlineStroke: isDark ? '#47474e' : '#CBD1D9', // slate-600 / gray-300

      // Tooltip colors
      tooltipBg: isDark ? '#232327' : '#F5F5F5', // slate-800 / white
      tooltipBorder: isDark ? '#2A2A2E' : '#EAEAEA', // slate-600 / light gray
      tooltipText: isDark ? '#A1A1AA' : '#7D7D93', // slate-100 / dark
      tooltipValue: isDark ? '#FFFFFF' : '#141437', // slate-100 / dark
      tooltipSecondaryText: isDark ? '#A1A1AA' : '#7D7D93', // slate-400 / gray-600
      tooltipDivider: '#475569', // slate-600 / light gray

      // Legend colors
      legendText: isDark ? '#e4e4e7' : '#1a1a1a', // slate-200 / dark
    };
  }

  private observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.renderChart());
    this.resizeObserver.observe(this.chartContainer?.nativeElement);
  }

  private clearChart() {
    select(this.chartContainer?.nativeElement).selectAll('*').remove();
  }

  private renderChart(): void {
    if (!this.data1?.length) return;

    this.clearChart();

    const container = this.chartContainer?.nativeElement;
    const parent = container?.parentElement?.parentElement;
    if (!parent) return;

    // Get theme colors
    const colors = this.getThemeColors();

    const width = parent.clientWidth;
    const height = Math.max(parent.clientHeight + this.chartHeight, this.minHeight);
    const maxBarHeight = height * 0.55;
    const topPadding = (height - maxBarHeight) / 2;

    const allData = [...this.data1, ...this.data2];
    const maxTotal = max(allData, (d: any) => d[this.valueKey] || 0) || 1;

    const yScale = scaleLinear()
      .domain([0, maxTotal]) // Add buffer to top
      .range([height - this.margin.bottom, topPadding]);

    // const xScale = scaleBand()
    //   .domain(this.data1.map((_, i) => i.toString()))
    //   .range([10, width - this.margin.right])
    //   .padding(0.2);

    const xScale = scaleBand()
      .domain(this.data1.map((d) => d?.['label']))
      .range([this.margin.left, width - this.margin.right]) // ⬅️ was [10, width - this.margin.right]
      .padding(0.2);

    const svg = select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('version', '1.1')
      .style('max-width', '100%')
      .style('height', 'auto');

    this.combineData(colors);
    this.fullBarWidth = xScale.bandwidth();

    const tooltip = select(container)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none');

    // Draw bars
    let ymax = 32;
    this.drawAxes(svg, xScale, yScale, height, colors);

    // Create gradients if needed
    if (this.barStyle === 'gradient') {
      const defs = svg.append('defs');
      
      this.combinedData.forEach((barGroup, i) => {
        barGroup.forEach((bar, j) => {
          const gradientId = `${this.chartId}-gradient-${i}-${j}`;
          const color = bar.color;
          
          const gradient = defs.append('linearGradient')
            .attr('id', gradientId)
            .attr('x1', '0%')
            .attr('y1', this.direction === 'bottom-to-top' ? '100%' : '0%')
            .attr('x2', '0%')
            .attr('y2', this.direction === 'bottom-to-top' ? '55%' : '45%');

          gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', color)
            .attr('stop-opacity', 0.6);

          gradient.append('stop')
            .attr('offset', '33%')
            .attr('stop-color', color)
            .attr('stop-opacity', 0.75);

          gradient.append('stop')
            .attr('offset', '66%')
            .attr('stop-color', color)
            .attr('stop-opacity', 0.9);

          gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', color)
            .attr('stop-opacity', 1);
        });
      });
    }

    this.combinedData.forEach((barGroup, i) => {
      // const groupX = xScale(i.toString())!;
      const groupX = xScale(barGroup[0]?.['label'])!;
      const group = svg
        .append('g')
        .attr('transform', `translate(0, -${this.margin.bottom})`)
        .attr('class', `bar-cluster-${i} bar-cluster`);

      const groupSize = barGroup.length;
      const innerGap = this.gap + 3;
      let dynamicBarWidth = this.fullBarWidth;
      

      if (groupSize > 1) {
          dynamicBarWidth = groupSize >= 3
            ? this.fullBarWidth / 3 - innerGap
            : this.fullBarWidth / (groupSize + 1) - innerGap;
      }

      // ⬅️ Apply width scaling
      dynamicBarWidth = dynamicBarWidth * this.barWidthScale;
      dynamicBarWidth = Math.min(dynamicBarWidth, 40); // Increased max width from 40 to 60 to reduce gaps

      const totalBarsWidth =
        groupSize * dynamicBarWidth + (groupSize - 1) * innerGap;
      const xOffset = (this.fullBarWidth - totalBarsWidth) / 2;

      barGroup.forEach(
        (bar: BarChartData & { color: colorLegend }, j: number) => {
          const value = bar[this.valueKey] || 0;
          const x = groupX + xOffset + j * (dynamicBarWidth + innerGap);
          let y = yScale(value);
          let h = yScale(0) - y;

          if (value < 5) {
            y = y - 3;
            h = h + 3;
          }

          // HOVER SHADOW (bgRect) - Now relative to the bar
          const bgRect = group
            .append('rect')
            .attr('x', x - 5)
            .attr('y', y - 5)
            .attr('width', dynamicBarWidth + 10)
            .attr('height', h + 10)
            .attr('fill', 'transparent')
            .attr('rx', 6)
            .attr('ry', 6)
            .style('cursor', 'pointer')
            .lower(); // Keep it behind the bar

          /* Removed group.on listeners here, moving to subgroup.on */

          const barPath = this.drawRoundedBar(
            x,
            y,
            dynamicBarWidth,
            h,
            6,
            this.otherKey || this.flatBars ? false : true,
            this.otherKey || this.flatBars ? false : true
          );

          const subgroup = group.append('g').attr('class', 'bar-group');
          if (!this.otherKey) {
            const fill = this.barStyle === 'gradient' 
              ? `url(#${this.chartId}-gradient-${i}-${j})`
              : bar.color;
              
            subgroup.append('path')
              .attr('d', barPath)
              .attr('fill', fill)
              .attr('fill-opacity', this.barStyle === 'gradient' ? 1 : 1);
          }

          if (this.otherKey) {
            const baseY = yScale(0);
            const outlineHeight = baseY - y;

            if (ymax > y) ymax = y + 55;

            const outlinePath = this.drawRoundedBar(
              x + 0.25,
              ymax + 30,
              dynamicBarWidth,
              yScale(0) - topPadding + 18,
              6,
              true,
              true
            );

            subgroup
              .append('path')
              .attr('d', outlinePath)
              .attr('fill', colors.barOutlineFill)
              .attr('stroke', colors.barOutlineStroke)
              .attr('stroke-width', 2)
              .attr('vector-effect', 'non-scaling-stroke');

            subgroup.append('path').attr('d', barPath).attr('fill', bar.color);

            subgroup
              .append('text')
              .attr('x', x + dynamicBarWidth / 2)
              .attr('y', 55)
              .attr('text-anchor', 'middle')
              .attr('fill', colors.barTextColor)
              .attr('font-weight', y + h - 20 < y ? '600' : '700')
              .attr('font-size', '10px')
              .attr('cursor', 'pointer')
              .text(bar[this.otherKey] + this.valueType);
          }

          if (this.showBarValue) {
            subgroup
              .append('text')
              .attr('x', x + dynamicBarWidth / 2)
              .attr('y', this.barTextposition == 'top' ? y - 5 : y + h - 20)
              .attr('text-anchor', 'middle')
              .attr(
                'fill',
                y + h - 20 <= y ? colors.barTextColor : colors.barTextColor
              )
              .attr('font-weight', y + h - 20 < y ? '600' : '700')
              .attr('font-size', '10px')
              .attr('cursor', 'pointer')
              .text(value);
          }

          subgroup.on('mouseover', () => {
            bgRect.transition().duration(200).attr('fill', colors.barOutlineFill);
            tooltip

              .html(
                this.getToolTipHtml(
                  bar,
                  this.tooltipData,
                  this.datePipe,
                  colors
                )
              )
              .style('opacity', 1);
          });

          subgroup.on('mousemove', (event: MouseEvent) => {
            const [ptrX, ptrY] = pointer(event, container);
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const tooltipHeight = tooltipNode.getBoundingClientRect().height;

            const horizontalOffset = 15;
            const verticalOffset = 5;

            let tooltipLeft = ptrX + horizontalOffset;
            if (tooltipLeft + tooltipWidth > width) {
              tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
            }

            let tooltipTop = ptrY - verticalOffset - tooltipHeight;
            if (tooltipTop < 0) tooltipTop = 0;

            tooltip
              .style('left', `${tooltipLeft}px`)
              .style('top', `${tooltipTop}px`);
          });

          subgroup.on('mouseleave', () => {
            bgRect.transition().duration(200).attr('fill', 'transparent');
            tooltip.style('opacity', 0);
          });
        }
      );
    });

    this.appendLegends(svg, height, colors);
  }

  private appendDefs(svg: Selection<SVGSVGElement, unknown, null, undefined>) {
    const defs = svg.append('defs');

    const createGradient = (id: string, stops: [string, string][]) => {
      const gradient = defs
        .append('linearGradient')
        .attr('id', id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      stops.forEach(([offset, color]) => {
        gradient
          .append('stop')
          .attr('offset', offset)
          .attr('stop-color', color);
      });
    };

    createGradient('gradient-normal', [
      ['0%', '#E7EFF8'],
      ['100%', '#FFFFFF'],
    ]);
    createGradient('gradient-reversed', [
      ['0%', '#FFFFFF'],
      ['100%', '#E7EFF8'],
    ]);
  }

  private combineData(colors: ReturnType<typeof this.getThemeColors>) {
    const maxLength = Math.max(this.data1.length, this.data2.length);
    this.combinedData = [];

    for (let i = 0; i < maxLength; i++) {
      let data: (BarChartData & {
        color: colorLegend;
        seriesIndex?: number;
      })[] = [];

      if (this.data1.length && this.data1[i]) {
        // Added check for data existence
        data.push({
          ...this.data1[i],
          color: (this.data1[i].color as colorLegend) || (colors.primaryBar as colorLegend),
          seriesIndex: 0,
        });
      }

      if (this.data2.length && this.data2[i]) {
        // Added check for data existence
        data.push({
          ...this.data2[i],
          color: (this.data2[i].color as colorLegend) || (colors.secondaryBar as colorLegend),
          seriesIndex: 1,
        });
      }

      this.combinedData.push(data);
    }
  }

  private drawAxes(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,

    x: ScaleBand<string>,
    y: ScaleLinear<number, number>,
    height: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    // const tickLabels = this.combinedData.map((item: any) =>
    //   Array.isArray(item)
    //     ? item[0].month_short
    //       ? item[0].month_short
    //       : this.labelKey == 'week'
    //         ? this.getWeeksBetween(item[0].start, item[0].end)
    //         : dayjs(item[0].start).format('MMM YYYY')
    //     : typeof item == 'object'
    //       ? item.month_short
    //         ? item.month_short
    //         : this.labelKey == 'week'
    //           ? this.getWeeksBetween(item.start, item.end)
    //           : dayjs(item.start).format('MMM YYYY')
    //       : ''
    // );

    const tickLabels = this.combinedData.map((item: any) => {
      const baseItem = Array.isArray(item) ? item[0] : item;

      // show business name
      if (this.showNameLabel && baseItem?.label) {
        return baseItem.label?.length > 15
          ? baseItem.label.slice(0, 15) + '...'
          : baseItem.label;
      }

      // show month_short (if available)
      if (baseItem?.month_short) {
        return baseItem.month_short;
      }

      // show week label
      if (this.labelKey === 'week' && baseItem?.start && baseItem?.end) {
        return this.getWeeksBetween(baseItem.start, baseItem.end);
      }

      // default: formatted month-year
      return dayjs(baseItem?.start).format('MMM YYYY');
    });

    // const xAxisGroup = svg
    //   .append('g')
    //   .attr('transform', `translate(0,${height - 2 * this.margin.bottom})`)
    //   .call(

    //     axisBottom(x)
    //       .tickSize(0)
    //       .tickValues(x.domain())
    //       .tickFormat((d, i) => tickLabels[i] ?? '')
    //   );

    const xAxisGroup = svg
      .append('g')
      .attr('transform', `translate(0,${height - 2 * this.margin.bottom})`)
      .call(
        axisBottom(x)
          .tickSize(0)
          .tickValues(x.domain())
          .tickFormat((label: string) => {
            if (!this.showFullLengthLabel) {
              // shorten label if needed
              return label.length > 15 ? label.slice(0, 15) + '...' : label;
            }

            // show full label
            return label;
          })
      );

    xAxisGroup
      .select('path.domain')
      .attr('stroke', colors.axisLine)
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 0.6);

    if (!this.rotateLabel) {
      xAxisGroup
        .selectAll('g.tick text')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', colors.axisText)
        .style('font-size', '14px')
        .style('font-weight', 500)
        .style('font-family', 'Inter, sans-serif');

      let [x0, x1] = this.getOffsetValues(svg);

      xAxisGroup
        .append('line')
        .attr('x1', x0)
        .attr('x2', x1)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);
    }

    // ROTATE LABELS

    if (this.rotateLabel) {
      xAxisGroup
        .selectAll('text')
        .attr('transform', 'rotate(-35)')
        .style('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0.5em')
        .attr('fill', colors.axisText)
        .style('font-size', '12px')
        .style('font-family', 'Lato, sans-serif')
        .style('font-weight', 500);

      // BASELINE LINE
      let [x0, x1] = this.getOffsetValues(svg);
      xAxisGroup
        .append('line')
        .attr('x1', x0)
        .attr('x2', x1)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);
    }

    if (this.showYAxis) {
      const yAxis = axisLeft(y).tickSizeOuter(0);

      if (this.yAxisShowOnlyFirstLast) {
        const yTicks = y.ticks();
        if (this.showAllYticks) {
          yAxis.tickValues(yTicks);
        } else if (yTicks && yTicks.length > 1) {
          yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
        }
      }

      const yAxisGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${this.margin.left - 10}, ${height - 2 * this.margin.bottom - y(0)
          })`
        )
        .call(yAxis);

      yAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisText)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');

      yAxisGroup
        .selectAll('line')
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 0.6);

      yAxisGroup.selectAll('path.domain').remove();

      const [yStart, yEnd] = y.range();
      yAxisGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', yStart)
        .attr('y2', yEnd)
        .attr('stroke', colors.axisLine || '#000')
        .attr('stroke-width', 2)
        .attr('shape-rendering', 'crispEdges');
    }

    const yGrid = axisLeft(y)
      .tickSize(-(x.range()[1] - this.margin.left)) // full width grid lines
      .tickFormat(() => '') // no labels
      .ticks(5); // number of horizontal lines

    svg
      .append('g')
      .attr('class', 'y-grid')
      .attr(
        'transform',
        `translate(${this.margin.left - 10}, ${height - 2 * this.margin.bottom - y(0)
        })`
      )
      .call(yGrid)
      .selectAll('line')
      .attr('stroke', colors.axisGridLine)
      .attr('stroke-opacity', 1)
      .attr('stroke-dasharray', '4 4');
    svg.select('.y-grid').select('path.domain').remove();

    const xGrid = axisBottom(x)
      .tickSize(-(height - this.margin.bottom * 3.5))
      .tickFormat(() => '');

    svg
      .append('g')
      .attr('class', 'x-grid')
      .attr('transform', `translate(0, ${height - this.margin.bottom * 2})`)
      .call(xGrid)
      .selectAll('line')
      .attr('stroke', colors.axisGridLine)
      .attr('stroke-opacity', 1)
      .attr('stroke-dasharray', '4 4');
    svg.select('.x-grid').select('path.domain').remove();
  }

  private drawRoundedBar(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    roundTopLeft = false,
    roundTopRight = false
  ): string {
    const right = x + width;
    const bottom = y + height;

    return [
      `M${x},${bottom}`,
      `V${y + (roundTopLeft ? radius : 0)}`,
      roundTopLeft
        ? `a${radius},${radius} 0 0 1 ${radius},-${radius}`
        : `V${y}`,
      `H${right - (roundTopRight ? radius : 0)}`,
      roundTopRight
        ? `a${radius},${radius} 0 0 1 ${radius},${radius}`
        : `H${right}`,
      `V${bottom}`,
      `H${x}`,
      `Z`,
    ].join(' ');
  }

  appendLegends(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    height: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    if (!this.legendData) {
      return;
    }

    let [x0, x1] = this.getOffsetValues(svg);

    const legend = svg
      .insert('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${x0 || 0}, ${height - 15})`);

    const legendItem = legend
      .selectAll('.legend-item')
      .data(this.legendData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(${i * 70}, 0)`);

    legendItem
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 12)
      .attr('height', 12)
      .attr('rx', 2)
      .attr('ry', 2)
      .attr('fill', (d) => d.color);

    legendItem
      .append('text')
      .attr('x', 18)
      .attr('y', 10)
      .text((d) => d.year)
      .style('font-size', '12px')
      .style('fill', colors.legendText);
  }

  getOffsetValues(svg: any) {
    const bars = svg.selectAll('.bar-cluster');
    let x0 = 0,
      x1 = 0;
    if (!bars.empty()) {
      const firstBar = bars.nodes()[0];
      const lastBar = bars.nodes()[bars.size() - 1];

      x0 = parseFloat(select(firstBar).select('rect').attr('x') || '0');
      x1 =
        parseFloat(select(lastBar).select('rect').attr('x') || '0') +
        parseFloat(select(lastBar).select('rect').attr('width') || '0');
    }

    return [x0, x1];
  }

  // getToolTipHtml(
  //   bar: BarChartData & { color: colorLegend },
  //   tooltipData: TooltipConfig[],
  //   datePipe: DatePipe,
  //   colors: ReturnType<typeof this.getThemeColors>
  // ) {
  //   let html = `<div class="w-[150px] p-2 rounded-2xl shadow-sm" style="
  //     background-color: ${colors.tooltipBg};
  //     border: 1px solid ${colors.tooltipBorder};
  //     padding: 1rem !important;
  //   ">
  //             <div style="border-bottom: 2px solid ${colors.tooltipDivider};">
  //               <span style="color: ${colors.tooltipText};">${datePipe.transform(bar.start, 'yyyy')}</span>
  //             </div>`;

  //   tooltipData.forEach((item: any) => {
  //     if (bar[item.key] || bar[item.key] === 0) {
  //       html += `<div class="text-sm pt-1 flex items-center justify-between">
  //                             <span class="pr-2" style="color: ${colors.tooltipSecondaryText};">${item.label}</span>
  //                             <span class="font-normal text-right" style="color: ${colors.tooltipText};">${item.key === this.valueKey
  //           ? bar[item.key] + this.valueKey
  //           : bar[item.key]
  //         }</span>
  //                           </div>`;
  //     }
  //   });

  //   return html + `</div>`;
  // }

  getToolTipHtml(
    bar: any,
    tooltipData: TooltipConfig[],
    datePipe: DatePipe,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const customLabel =
      this.tooltipValueLabels && this.tooltipValueLabels[bar.seriesIndex];
    const defaultLabel = this.showNameLabel ? 'Contracts' : 'Active Businesses';
    const label = customLabel || defaultLabel;

    return `
    <div style="
      background: ${colors.tooltipBg};
      border: 1px solid ${colors.tooltipBorder};
      padding: 12px 16px;
      border-radius: 16px;
      min-width: 180px;
    ">
      <div style="
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 2px;
        color: ${colors.tooltipText};
      ">
        ${this?.showNameLabel
        ? bar.label
        : bar?.label
          ? bar?.label
          : bar.start
            ? datePipe.transform(bar.start, 'MMM yyyy')
            : ''
      }
      </div>

      <div style="
        font-size: 16px;
        font-weight: 400;
        color: ${colors.tooltipValue};
      ">
       ${label}: ${bar[this.valueKey]}
      </div>
    </div>
  `;
  }

  getWeeksBetween(startDate: any, endDate: any) {
    const year = dayjs(startDate).year();
    const start = dayjs(startDate).isoWeekday(1);
    const end = dayjs(endDate).isoWeekday(7);

    const weeks: { weekNumber: number }[] = [];

    let current = start.clone();

    while (current.isSame(end) || current.isBefore(end)) {
      weeks.push({
        weekNumber: current.isoWeek(),
        // weekStart: current.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        // weekEnd: current.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      });

      current = current.add(1, 'week');
    }

    return 'week ' + (weeks.length > 0 ? weeks[0].weekNumber : ''); // Added safety check
  }
}
